import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import React, { SyntheticEvent, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../../hooks/hooks";
import { useFormik } from "formik";
import * as Yup from "yup";

import Spinner from "../../../../reusableComponents/spinner/spinner";

import {
  getTrainingSchools,
  getTrainingDomains,
} from "../../../../../store/thunks/portalManagementThunk";

import {
  editTrainingUser
} from "../../../../../store/thunks/systemManagementThunk";

import type { TrainingUserInfo } from "../userManagementModel";

import { RoleIndex } from "../../../../../models/role";
import { UserType } from "../../../../../models/userType";

type ErrorState = Record<
  "user_role" | "user_school" | "user_domain" | "global",
  { hasError: boolean; message: string }
>;

const EditUserDialog = ({
    open,
    handleEditClose,
    editUserData,
  } : {
    open: boolean;
    handleEditClose: (message?: string) => void;
    editUserData: any;
  }) => {

  const [showSchools, setShowSchools] = useState(false);
  const [showDomains, setShowDomains] = useState(false);
  const [isSchoolUser, setIsSchoolUser] = useState(false);
  const [isDealerUser, setIsDealerUser] = useState(false);

  const dispatch = useAppDispatch();

  const {
    editTrainingUserLoading,
  } = useAppSelector((s: any) => s.systemManagement);

  const {
    trainingRoles,
    trainingSchools,
    trainingDomains
  } = useAppSelector((s: any) => s.portalManagement);
  
  const [errors, setErrors] = useState<ErrorState>({
    global: { hasError: false, message: "" },
    user_role: { hasError: false, message: "" },
    user_domain: { hasError: false, message: "" },
    user_school: { hasError: false, message: "" }
  });

  const clearErrors = () =>
    setErrors({
      global: { hasError: false, message: "" },
      user_role: { hasError: false, message: "" },
      user_school: { hasError: false, message: "" },
      user_domain: { hasError: false, message: "" }
    });

  const setError = (field: keyof ErrorState, message: string) =>
    setErrors((prev) => ({ ...prev, [field]: { hasError: !!message, message } }));

  const validationSchema = Yup.object({
    email: Yup.string().email("Invalid email").required("Required"),
    first_name: Yup.string().min(1).max(50).required("Required"),
    last_name: Yup.string().min(1).max(50).required("Required"),
    user_role: Yup.string().min(1).max(50).required("Required"),
    user_school: Yup.string().nullable().when("user_role", {
      is: (val: any) => val === UserType.Name.LWS_SCHOOL,
      then: (schema) => schema.nonNullable("School is required").required("School is required"),
      otherwise: (schema) => schema.optional(),
    }),
    user_domain: Yup.string().nullable().when("user_role", {
      is: (val: any) => val === UserType.Name.LWS_DEALER,
      then: (schema) => schema.nonNullable("Domain is required").required("Domain is required"),
      otherwise: (schema) => schema.optional(),
    })
  })

  const editUserForm = useFormik<TrainingUserInfo>({
    initialValues: {
      email: "",
      first_name: "",
      last_name: "",
      role_index: RoleIndex.ClientUser,
      user_role: null,
      user_school: null,
      user_domain: null,
      user_admin: null
    },

    validationSchema,

    enableReinitialize: true,

    onSubmit: (values) => {
      clearErrors();

      const payload: any = {
        user_index: editUserData?.clients_user_id,
        update_type: [],
      };

      // Name change
      if (
        values.first_name !== editUserData?.first_name ||
        values.last_name !== editUserData?.last_name
      ) {
        payload.update_type.push("name");
        payload.current_first_name = editUserData?.first_name;
        payload.current_last_name = editUserData?.last_name;
        payload.new_first_name = values.first_name;
        payload.new_last_name = values.last_name;
      }

      // Role / client change
      const roleTypeChanged = values?.user_role !== editUserData?.role_type;

      if (roleTypeChanged) {
        payload.update_type.push("role_type");
        payload.new_user_role_type = values.user_role;
        payload.new_user_is_admin = values.user_admin ? UserType.Name.LWS_ADMIN : null;
        payload.new_client_user_type_index = UserType.Index[values.user_role as keyof typeof UserType.Index]
      }

      // School Dealer change
      const schoolChanged = isSchoolUser && (values?.user_school !== editUserData?.r_operand)

      if (schoolChanged || roleTypeChanged) {
        payload.update_type.push("school");
        payload.new_user_school = values?.user_school;
      }

      // Dealer change
      const dealerChanged = isDealerUser && values?.user_domain !== editUserData?.r_operand;

      if (dealerChanged || roleTypeChanged) {
        payload.update_type.push("dealer");
        payload.new_user_domain = values?.user_domain;
      }

      if (!payload.update_type.length) {
        setError("global", "No user changes detected.");
        return;
      }

      dispatch(editTrainingUser({ editUserPayload: payload })).then((result: any) => {
        if (result.payload?.result === true) {
          handleEditClose(result.payload.message);
        } else {
          setError("global", "Failed to update user.");
        }
      });
    },
  });

  // Initialize form + fetch data
  useEffect(() => {
    if (!open || !editUserData) return;

    clearErrors();
    editUserForm.resetForm();

    dispatch(getTrainingSchools({}))
    dispatch(getTrainingDomains({}))

    setIsSchoolUser(editUserData?.role_type === UserType.Name.LWS_SCHOOL)
    setIsDealerUser(editUserData?.role_type === UserType.Name.LWS_DEALER)
    
    editUserForm.setValues({
      email: editUserData.user_name || "",
      first_name: editUserData.first_name || "",
      last_name: editUserData.last_name || "",
      role_index: RoleIndex.ClientUser,
      user_role: editUserData.role_type,
      user_school: isSchoolUser ? editUserData.r_operand : null,
      user_domain: isDealerUser ? editUserData.r_operand : null,
      user_admin: (!isDealerUser && !isSchoolUser) || null
    });

    setShowSchools(isSchoolUser);
    setShowDomains(isDealerUser);
    
  }, [open, editUserData, isDealerUser, isSchoolUser]);

  // ────────────────────────────────────────────────
  // Handlers
  // ────────────────────────────────────────────────
  const handleRoleChange = (_: SyntheticEvent, role: any) => {
    clearErrors();

    ["user_school", "user_domain", "user_admin"].forEach(field => {
      editUserForm.setFieldError(field, undefined);
    });

    if(!role) return;

    editUserForm.setFieldValue("user_role", role);

    if(role === UserType.Name.LWS_SCHOOL) {
      setShowSchools(true);
      setShowDomains(false);
    } 
    if(role === UserType.Name.LWS_DEALER) {
      setShowDomains(true);
      setShowSchools(false);
    }
    // LWS_SUPPORT gets the same all-records scope as LWS_ADMIN (user_admin
    // drives the backend's 1=1 people_user scope); only user management differs.
    if(role === UserType.Name.LWS_ADMIN || role === UserType.Name.LWS_SUPPORT) {
      editUserForm.setFieldValue("user_admin", true);
      setShowDomains(false);
      setShowSchools(false);
    }
  };

  const handleSchoolChange = (_: SyntheticEvent, school: any) => {
    clearErrors();
    editUserForm.setFieldValue("user_school", school, false);
    editUserForm.setFieldTouched("user_school", false, false);
    editUserForm.setFieldError("user_school", undefined);
    setShowDomains(false);
    ["user_domain", "user_admin"].forEach(f => {
      editUserForm.setFieldValue(f, null, false);
      editUserForm.setFieldTouched(f, false, false);
      editUserForm.setFieldError(f, undefined);
    });
  };

  const handleDomainChange = (_: SyntheticEvent, domain: any) => {
    clearErrors();
    editUserForm.setFieldValue("user_domain", domain, false);
    editUserForm.setFieldTouched("user_domain", false, false);
    editUserForm.setFieldError("user_domain", undefined);
    setShowSchools(false);
    ["user_school", "user_admin"].forEach(f => {
      editUserForm.setFieldValue(f, null, false);
      editUserForm.setFieldTouched(f, false, false);
      editUserForm.setFieldError(f, undefined);
    });
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={(_, reason) => reason !== "backdropClick" && handleEditClose()}
      disableEscapeKeyDown
    >
      <DialogTitle sx={{ bgcolor: "#3B934A", color: "white", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h5" fontWeight="bold" component="span">
          Edit User
        </Typography>
        <IconButton onClick={() => handleEditClose()} sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Spinner loading={editTrainingUserLoading} />

      <form onSubmit={editUserForm.handleSubmit} method="post">
        <DialogContent>
          <Grid container spacing={2} sx={{ p: 2 }}>
            {/* Email (disabled) */}
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Email" value={editUserForm.values.email} disabled />
            </Grid>

            {/* Names */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="First Name"
                name="first_name"
                value={editUserForm.values.first_name}
                onChange={editUserForm.handleChange}
                error={editUserForm.touched.first_name && !!editUserForm.errors.first_name}
                helperText={editUserForm.touched.first_name && typeof editUserForm.errors.first_name === "string" ? editUserForm.errors.first_name : ""}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={editUserForm.values.last_name}
                onChange={editUserForm.handleChange}
                error={editUserForm.touched.last_name && !!editUserForm.errors.last_name}
                helperText={editUserForm.touched.last_name && typeof editUserForm.errors.last_name === "string" ? editUserForm.errors.last_name : ""}
              />
            </Grid>

            {/* Role */}
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={trainingRoles?.map((r: any) => r.role_type) || []}
                // getOptionLabel={(opt) => opt}
                value={editUserForm.values.user_role}
                onChange={handleRoleChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Role"
                    error={errors.user_role.hasError || (editUserForm.touched.user_role && !!editUserForm.errors.user_role)}
                    helperText={errors.user_role.message || (editUserForm.touched.user_role && typeof editUserForm.errors.user_role === "string" ? editUserForm.errors.user_role : "")}
                  />
                )}
              />
            </Grid>

            {showSchools && (<Grid item xs={12} sm={6}>
              <Autocomplete
                options={trainingSchools.map((s: any) => s.name) || []}
                value={editUserForm.values.user_school}
                onChange={handleSchoolChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="School(s)"
                    error={errors.user_school.hasError || (editUserForm.touched.user_school && !!editUserForm.errors.user_school)}
                    helperText={errors.user_school.message || (editUserForm.touched.user_school && typeof editUserForm.errors.user_school === "string" ? editUserForm.errors.user_school : "")}
                  />
                )}
              />
            </Grid>)}

            {showDomains && (<Grid item xs={12} sm={6}>
              <Autocomplete
                options={Array.from(new Set((trainingDomains || []).map((e: any) => e.email.split('@')[1]?.split('.')[0])))}
                value={editUserForm.values.user_domain}
                onChange={handleDomainChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Domain(s)"
                    error={errors.user_domain.hasError || (editUserForm.touched.user_domain && !!editUserForm.errors.user_domain)}
                    helperText={errors.user_domain.message || (editUserForm.touched.user_domain && typeof editUserForm.errors.user_domain === "string" ? editUserForm.errors.user_domain : "")}
                  />
                )}
              />
            </Grid>)}

            {errors.global.hasError && (
              <Grid item xs={12}>
                <Typography color="error" align="center">
                  {errors.global.message}
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ borderTop: "1px solid #ddd", p: 2 }}>
          <Button variant="outlined" color="error" onClick={() => handleEditClose()}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary" disabled={editUserForm.isSubmitting || editTrainingUserLoading}>
            Save Changes
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditUserDialog;